<template>
  <div id="app">
    <v-app>
      <v-app-bar
        class="site-header"
        :elevation="scrolled ? 2 : 0"
        height="72"
        flat
        app
      >
        <div class="bh-container site-header__inner">
          <router-link to="/" class="site-brand" aria-label="Black Hills Mountain Lion Foundation home">
            <img
              class="site-brand__mark"
              :src="logoSrc"
              alt="Black Hills Mountain Lion Foundation"
              width="48"
              height="48"
            />
            <span class="site-brand__name">
              Black Hills Mountain Lion
              <em>Foundation</em>
            </span>
          </router-link>

          <nav class="site-nav d-none d-md-flex" aria-label="Primary">
            <router-link
              v-for="item in navItems"
              :key="item.to"
              class="site-nav__link"
              :to="item.to"
            >
              {{ item.title }}
            </router-link>
            <DonateButton
              class="site-nav__donate"
              variant="flat"
              color="primary"
              text="Donate"
            />
          </nav>

          <v-btn
            class="d-md-none site-header__menu"
            icon
            variant="text"
            aria-label="Open menu"
            @click="drawer = !drawer"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </div>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer" temporary location="right" width="280">
        <div class="drawer-brand">
          <img :src="logoSrc" alt="" width="40" height="40" />
          <span>BHMLF</span>
        </div>
        <v-list nav>
          <v-list-item
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            :title="item.title"
            @click="closeDrawer"
          />
          <v-list-item class="mt-2">
            <DonateButton variant="flat" color="primary" text="Donate" />
          </v-list-item>
        </v-list>
      </v-navigation-drawer>

      <v-main>
        <router-view />
      </v-main>

      <NonProfitFooter />
    </v-app>
  </div>
</template>

<script>
import DonateButton from "./components/DonateButton.vue";
import NonProfitFooter from "./components/NonProfitFooter.vue";

const NAV_ITEMS = [
  { to: "/", title: "Home" },
  { to: "/sponsor", title: "Sponsor" },
  { to: "/events", title: "Events" },
  { to: "/contact", title: "Contact" },
];

export default {
  name: "App",
  components: {
    DonateButton,
    NonProfitFooter,
  },
  data() {
    return {
      drawer: false,
      scrolled: false,
      logoSrc: require("@/assets/logo-mark.png"),
      navItems: NAV_ITEMS,
    };
  },
  mounted() {
    this.onScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.injectOrganizationStructuredData();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    closeDrawer() {
      this.drawer = false;
    },
    onScroll() {
      this.scrolled = window.scrollY > 8;
    },
    injectOrganizationStructuredData() {
      if (document.getElementById("bhmlf-org-schema")) return;

      const script = document.createElement("script");
      script.id = "bhmlf-org-schema";
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "NonprofitOrganization",
        name: "Black Hills Mountain Lion Foundation",
        url: "https://blackhillsmountainlionfoundation.org/",
        description:
          "Preserving the balance of nature through education, conservation, and responsible hunting.",
        sameAs: [
          "https://www.facebook.com/people/Black-Hills-Mountain-Lion-Foundation/61556313550621/",
        ],
      });
      document.head.appendChild(script);
    },
  },
};
</script>

<style>
.site-header {
  background: rgba(243, 245, 242, 0.92) !important;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid transparent;
  transition: box-shadow 0.2s ease;
}

.site-header.v-toolbar {
  color: var(--bh-ink);
}

.v-toolbar__content {
  padding: 0 !important;
}

.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(100% - 2rem, var(--bh-container));
  margin-inline: auto;
  gap: 1rem;
  height: 72px;
}

.site-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: var(--bh-forest);
  min-width: 0;
}

.site-brand__mark {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
}

.site-brand__name {
  font-family: var(--bh-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.01em;
  text-align: left;
}

.site-brand__name em {
  display: block;
  font-style: normal;
  color: var(--bh-muted);
  font-weight: 500;
  font-size: 0.82rem;
}

.site-nav {
  align-items: center;
  gap: 0.35rem;
}

.site-nav__link {
  color: var(--bh-ink);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.55rem 0.85rem;
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.site-nav__link:hover,
.site-nav__link.router-link-active {
  color: var(--bh-forest);
  background: rgba(30, 58, 47, 0.08);
}

.site-nav__donate {
  margin-left: 0.35rem;
}

.site-header__menu {
  color: var(--bh-forest) !important;
}

.drawer-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1rem 0.5rem;
  font-family: var(--bh-font-display);
  font-weight: 600;
  color: var(--bh-forest);
}

.drawer-brand img {
  border-radius: 6px;
}

@media (max-width: 720px) {
  .site-brand__name {
    font-size: 0.85rem;
  }
}
</style>
